import type { SpotlightActionData } from '@mantine/spotlight';
import { Spotlight } from '@mantine/spotlight';
import { observer } from 'mobx-react-lite';
import type { JSX, ReactNode } from 'react';
import React, { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import { useFlags, useLocalUrl } from '@feathr/extender/state';

import { usePrimaryNavRoutes } from './usePrimaryNavRoutes';

import '@mantine/spotlight/styles.css';
import * as styles from './CommandPalette.css';

interface IProps {
  children: ReactNode;
}

function CommandPalette({ children }: Readonly<IProps>): JSX.Element {
  const localUrl = useLocalUrl();
  const history = useHistory();
  const { routes: availableLocations, refreshRoutes } = usePrimaryNavRoutes();
  const { showCommandPalette } = useFlags();
  const wasDisabledRef = useRef(true);

  useEffect(() => {
    if (!showCommandPalette) {
      wasDisabledRef.current = true;
      return;
    }

    if (wasDisabledRef.current) {
      refreshRoutes();
      wasDisabledRef.current = false;
    }
  }, [showCommandPalette, refreshRoutes]);

  const actions: SpotlightActionData[] = availableLocations.map((route) => ({
    id: route.id,
    label: route.label,
    description: route.description,
    keywords: route.keywords,
    onClick: () => history.push(localUrl(route.path)),
  }));

  return (
    <>
      {children}
      <Spotlight
        actions={actions}
        classNames={{
          action: styles.action,
          actionsList: styles.actionsList,
          actionLabel: styles.actionLabel,
        }}
        disabled={!showCommandPalette}
        highlightQuery
        limit={10}
        maxHeight={350}
        nothingFound={'No locations found...'}
        scrollable
        searchProps={{
          placeholder: 'Search locations...',
        }}
        shortcut={['mod + K']}
        // TODO: (refactor) No in-line styles. Move styles to CSS file
        styles={{
          actionLabel: {
            fontWeight: 600,
            fontSize: '14px',
          },
          actionDescription: {
            fontSize: '12px',
            opacity: 0.65,
            fontWeight: 400,
          },
        }}
      />
    </>
  );
}

export default observer(CommandPalette);
